import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  query,
  getDocs,
  orderBy,
  limit,
  startAfter,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faSearch,
  faDownload,
  faChevronDown,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import Papa from "papaparse";

const PAGE_SIZE = 50;

// Fungsi untuk mengformat tanggal menjadi humanized
const formatHumanizedDate = (isoDateString) => {
  if (!isoDateString || isoDateString === "-") return "-";

  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return "-";

  const now = new Date();
  const diffInMs = now - date;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInDays >= 7) {
    // If more than 7 days, show full date
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } else if (diffInDays >= 1) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  } else if (diffInHours >= 1) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else if (diffInMinutes >= 1) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  } else {
    return "Just now";
  }
};

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse w-4"></div>
    </td>
  </tr>
));

export const Archived = ({ user }) => {
  const { showNotification } = useNotification();
  const [state, setState] = useState({
    archivedRecords: [],
    filteredRecords: [],
    searchQuery: "",
    loading: true,
    loadingMore: false,
    lastVisible: null,
    hasMore: true,
    totalRecords: 0,
    totalFiltered: 0,
    sortKategoriDirection: null,
    allRecords: [],
  });
  const [viewRecord, setViewRecord] = useState(null);

  const db = getFirestore();
  const isMounted = useRef(true);

  // Log user info for debugging
  useEffect(() => {
    console.log("[DEBUG] Current user:", {
      uid: user?.uid,
      email: user?.email,
    });
    const checkAdminRole = async () => {
      if (user?.uid) {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            console.log("[DEBUG] User document:", userDoc.data());
          } else {
            console.log("[DEBUG] User document does not exist in /users");
          }
        } catch (err) {
          console.error("[ERROR] Error fetching user document:", err);
        }
      } else {
        console.log("[DEBUG] No user is logged in");
      }
    };
    checkAdminRole();
  }, [user]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch archived records with pagination
  const loadRecords = useCallback(
    async (lastDoc = null, sortDirection = state.sortKategoriDirection) => {
      if (!isMounted.current) return;

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        archivedRecords: lastDoc ? prev.archivedRecords : [],
        filteredRecords: state.searchQuery ? prev.filteredRecords : [],
      }));

      try {
        const archivedCollection = collection(db, "archivedRegistrations");
        const constraints = [
          where("originalCollection", "==", "pendaftaranJOTR"),
          orderBy("namaLengkap"),
        ];
        if (sortDirection) {
          constraints.unshift(orderBy("kategori", sortDirection));
        }
        const baseQuery = lastDoc
          ? query(
              archivedCollection,
              ...constraints,
              startAfter(lastDoc),
              limit(PAGE_SIZE)
            )
          : query(archivedCollection, ...constraints, limit(PAGE_SIZE));

        console.log("[DEBUG] Executing query for loadRecords:", constraints);
        const snapshot = await getDocs(baseQuery);
        console.log("[DEBUG] Query snapshot size:", snapshot.size);
        const recordsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          archivedAt: doc.data().archivedAt?.toDate(),
          archivedAtIso: doc.data().archivedAt?.toDate().toISOString() || "-",
        }));

        if (!isMounted.current) return;

        setState((prev) => {
          const updatedRecords = lastDoc
            ? [...prev.archivedRecords, ...recordsList]
            : recordsList;
          return {
            ...prev,
            archivedRecords: updatedRecords,
            filteredRecords: state.searchQuery
              ? prev.filteredRecords
              : updatedRecords,
            lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
            loading: false,
            loadingMore: false,
            hasMore: snapshot.docs.length === PAGE_SIZE,
            totalFiltered: state.searchQuery
              ? prev.totalFiltered
              : prev.totalRecords,
          };
        });
      } catch (err) {
        console.error("[ERROR] Error fetching archived records:", err);
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
          }));
          showNotification(
            "error",
            "Failed to load archive data. Please try again."
          );
        }
      }
    },
    [state.searchQuery, state.sortKategoriDirection, showNotification]
  );

  // Fetch total archived records count
  const fetchTotalRecords = useCallback(async () => {
    try {
      const archivedCollection = collection(db, "archivedRegistrations");
      const q = query(
        archivedCollection,
        where("originalCollection", "==", "pendaftaranJOTR")
      );
      console.log("[DEBUG] Executing query for fetchTotalRecords");
      const snapshot = await getDocs(q);
      console.log("[DEBUG] Total records snapshot size:", snapshot.size);
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          totalRecords: snapshot.size,
          totalFiltered: state.searchQuery ? prev.totalFiltered : snapshot.size,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Error fetching total archived records:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Failed to load archive count. Please try again."
        );
      }
    }
  }, [state.searchQuery, showNotification]);

  // Fetch all archived records for search filtering
  const fetchAllRecords = useCallback(async () => {
    try {
      const archivedCollection = collection(db, "archivedRegistrations");
      const q = query(
        archivedCollection,
        where("originalCollection", "==", "pendaftaranJOTR")
      );
      console.log("[DEBUG] Executing query for fetchAllRecords");
      const snapshot = await getDocs(q);
      console.log("[DEBUG] All records snapshot size:", snapshot.size);
      const allRecords = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        archivedAt: doc.data().archivedAt?.toDate(),
        archivedAtIso: doc.data().archivedAt?.toDate().toISOString() || "-",
      }));
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allRecords,
          totalRecords: snapshot.size,
          totalFiltered: state.searchQuery ? prev.totalFiltered : snapshot.size,
        }));
      }
    } catch (err) {
      console.error("[ERROR] Error fetching all archived records:", err);
      if (isMounted.current) {
        showNotification(
          "error",
          "Failed to load all archive data. Please try again."
        );
      }
    }
  }, [state.searchQuery, showNotification]);

  // Handle search
  useEffect(() => {
    if (state.searchQuery) {
      const filtered = (state.allRecords || state.archivedRecords).filter((p) =>
        [p.namaLengkap, p.email, p.orderId, p.kategori, p.transaction_status]
          .filter(Boolean)
          .some((field) =>
            field.toLowerCase().includes(state.searchQuery.toLowerCase())
          )
      );
      setState((prev) => ({
        ...prev,
        filteredRecords: filtered.slice(0, PAGE_SIZE),
        fullFilteredList: filtered,
        totalFiltered: filtered.length,
        hasMore: filtered.length > PAGE_SIZE,
        lastVisible: null,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        filteredRecords: prev.archivedRecords,
        fullFilteredList: [],
        totalFiltered: prev.totalRecords,
        hasMore: prev.archivedRecords.length < prev.totalRecords,
      }));
    }
  }, [
    state.searchQuery,
    state.archivedRecords,
    state.allRecords,
    state.totalRecords,
  ]);

  // Initial data fetch
  useEffect(() => {
    loadRecords();
    fetchTotalRecords();
    fetchAllRecords();
  }, [loadRecords, fetchTotalRecords, fetchAllRecords]);

  // Load more records
  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;

    if (state.searchQuery) {
      const currentIndex = Math.floor(state.filteredRecords.length / PAGE_SIZE);
      const startIndex = currentIndex * PAGE_SIZE;
      const additionalRecords = state.fullFilteredList.slice(
        startIndex,
        startIndex + PAGE_SIZE
      );
      if (!additionalRecords.length) {
        setState((prev) => ({ ...prev, hasMore: false }));
        return;
      }
      setState((prev) => ({
        ...prev,
        filteredRecords: [...prev.filteredRecords, ...additionalRecords],
        hasMore: startIndex + PAGE_SIZE < prev.fullFilteredList.length,
      }));
    } else {
      loadRecords(state.lastVisible);
    }
  }, [
    state.loading,
    state.loadingMore,
    state.hasMore,
    state.searchQuery,
    state.filteredRecords,
    state.fullFilteredList,
    state.lastVisible,
    loadRecords,
  ]);

  // Handle kategori sort toggle
  const handleKategoriSortToggle = useCallback(() => {
    const newDirection =
      state.sortKategoriDirection === "desc" ? "asc" : "desc";
    setState((prev) => ({
      ...prev,
      sortKategoriDirection: newDirection,
      lastVisible: null,
      hasMore: true,
      archivedRecords: [],
      filteredRecords: [],
    }));
    loadRecords(null, newDirection);
  }, [state.sortKategoriDirection, loadRecords]);

  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      "Order ID",
      "Name",
      "Category",
      "Transaction Status",
      "Email",
      "Gender",
      "City",
      "Address",
      "Province",
      "Country",
      "Phone",
      "Identity Number",
      "Date of Birth",
      "Blood Type",
      "Emergency Contact",
      "Emergency Contact Name",
      "Emergency Contact Relationship",
      "Bib Name",
      "Bib Number",
      "Jersey Size",
      "Price",
      "Distance",
      "Archive Date",
    ];

    const rows = state.filteredRecords.map((p) => [
      p.orderId || "-",
      p.namaLengkap || "-",
      p.kategori || "-",
      p.transaction_status || "null",
      p.email || "-",
      p.gender || "-",
      p.kota || "-",
      p.alamat || "-",
      p.provinsi || "-",
      p.negara || "-",
      p.telepon || "-",
      p.nomorIdentitas || "-",
      p.tanggalLahir || "-",
      p.golonganDarah || "-",
      p.kontakDarurat || "-",
      p.namaKontakDarurat || "-",
      p.hubunganKontakDarurat || "-",
      p.bibName || "-",
      p.bibNumber || "-",
      p.sizeJersey || "-",
      p.harga || 0,
      p.distance || 0,
      p.archivedAtIso || "-", // Gunakan ISO string untuk CSV
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((field) =>
            typeof field === "string" && field.includes(",")
              ? `"${field}"`
              : field
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `archived_registrations_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    showNotification("success", "Archive data successfully exported to CSV.");
  };

  // Handle view record
  const handleView = (record) => {
    setViewRecord(record);
  };

  const rangeText = `${state.filteredRecords.length} of ${state.totalFiltered} Archive Records`;

  return (
    <section className="min-h-screen py-20">
      <div className="container mx-auto px-4">
        {/* Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-full max-w-md">
              <InputField
                name="search"
                type="text"
                value={state.searchQuery}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    searchQuery: e.target.value,
                    hasMore: true,
                  }))
                }
                placeholder="Search name, email, order ID, category, or status..."
                aria-label="Search archive data"
                startIcon={
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                }
              />
            </div>
            <div className="flex gap-4">
              <Button
                onClick={handleExportCSV}
                variant="warning"
                className="flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} />
                Export to CSV
              </Button>
            </div>
          </div>

        {/* Archived Records Table */}
        <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-lime-600"
                  onClick={handleKategoriSortToggle}
                  aria-label="Sort by category"
                >
                  <span className="flex items-center">
                    Category
                    {state.sortKategoriDirection && (
                      <FontAwesomeIcon
                        icon={
                          state.sortKategoriDirection === "asc"
                            ? faSortUp
                            : faSortDown
                        }
                        className="ml-1 text-gray-500"
                      />
                    )}
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Archive Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.filteredRecords.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    {state.searchQuery
                      ? "No archive records match the search."
                      : "No archive records available."}
                  </td>
                </tr>
              ) : (
                state.filteredRecords.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {p.orderId || "-"}
                    </td>
                    <td
                      className={`px-4 py-4 whitespace-nowrap uppercase text-sm ${
                        p.gender === "Female" || p.gender === "Perempuan"
                          ? "text-lime-600"
                          : "text-gray-600"
                      }`}
                    >
                      {p.namaLengkap ? p.namaLengkap.toUpperCase() : "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {p.kategori || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {p.transaction_status || "null"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatHumanizedDate(p.archivedAtIso)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleView(p)}
                        className="text-lime-600 hover:text-lime-900"
                        disabled={state.loading}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Load More and Count */}
        <div className="flex justify-end items-center px-4 py-2">
          <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
          <button
            onClick={loadMore}
            disabled={state.loading || state.loadingMore || !state.hasMore}
            className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
              ${
                state.loading || state.loadingMore || !state.hasMore
                  ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                  : "bg-lime-600 text-white hover:bg-lime-700"
              }`}
            aria-label={
              state.hasMore
                ? "Load more archive data"
                : "End of archive list"
            }
          >
            <span>{state.hasMore ? "Load more" : "End of list"}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>

        {/* Modal Detail Arsip */}
        {viewRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-lg py-6 px-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Archive Record Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                <p>
                  <strong>Order ID:</strong> {viewRecord.orderId || "-"}
                </p>
                <p>
                  <strong>Name:</strong> {viewRecord.namaLengkap || "-"}
                </p>
                <p>
                  <strong>Email:</strong> {viewRecord.email || "-"}
                </p>
                <p>
                  <strong>Category:</strong> {viewRecord.kategori || "-"}
                </p>
                <p>
                  <strong>Transaction Status:</strong>{" "}
                  {viewRecord.transaction_status || "null"}
                </p>
                <p>
                  <strong>Archive Date:</strong>{" "}
                  {formatHumanizedDate(viewRecord.archivedAtIso)}
                </p>
                <p>
                  <strong>Price:</strong>{" "}
                  {viewRecord.harga
                    ? `Rp ${viewRecord.harga.toLocaleString("en-US")}`
                    : "-"}
                </p>
                <p>
                  <strong>Distance:</strong>{" "}
                  {viewRecord.distance ? `${viewRecord.distance} KM` : "-"}
                </p>
                <p>
                  <strong>Address:</strong> {viewRecord.alamat || "-"}
                </p>
                <p>
                  <strong>City:</strong> {viewRecord.kota || "-"}
                </p>
                <p>
                  <strong>Province:</strong> {viewRecord.provinsi || "-"}
                </p>
                <p>
                  <strong>Country:</strong> {viewRecord.negara || "-"}
                </p>
                <p>
                  <strong>Phone:</strong> {viewRecord.telepon || "-"}
                </p>
                <p>
                  <strong>Identity Number:</strong>{" "}
                  {viewRecord.nomorIdentitas || "-"}
                </p>
                <p>
                  <strong>Date of Birth:</strong>{" "}
                  {viewRecord.tanggalLahir || "-"}
                </p>
                <p>
                  <strong>Gender:</strong> {viewRecord.gender || "-"}
                </p>
                <p>
                  <strong>Blood Type:</strong>{" "}
                  {viewRecord.golonganDarah || "-"}
                </p>
                <p>
                  <strong>Emergency Contact:</strong>{" "}
                  {viewRecord.kontakDarurat || "-"}
                </p>
                <p>
                  <strong>Emergency Contact Name:</strong>{" "}
                  {viewRecord.namaKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Emergency Contact Relationship:</strong>{" "}
                  {viewRecord.hubunganKontakDarurat || "-"}
                </p>
                <p>
                  <strong>Bib Name:</strong> {viewRecord.bibName || "-"}
                </p>
                <p>
                  <strong>BIB Number:</strong> {viewRecord.bibNumber || "-"}
                </p>
                <p>
                  <strong>Jersey Size:</strong> {viewRecord.sizeJersey || "-"}
                </p>
              </div>
              <div className="flex justify-center mt-6">
                <Button
                  onClick={() => setViewRecord(null)}
                  variant="secondary"
                  className="w-full max-w-xs"
                  disabled={state.loading}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
